import i18n from "../../../i18n";import { Trans } from "react-i18next";import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, ClipboardList, X, Edit3, Trash2 } from 'lucide-react';
import { AppLayout, PageBackLink } from '../../../components/layout';
import {
  PageHeader,
  EmptyState,
  LoadingState,
  ContentCard,
  ui } from
'../../../components/ui';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue } from
'../../../components/ui/select';
import {
  CHECKLIST_WHO_TO_CHECK,
  searchProductServices,
  searchComplaintsByProduct,
  fetchChecklistItemsByComplaintType,
  deleteChecklistTemplate } from
'../../../services/fsmService';
import { cn } from '../../../lib/utils';

const LIST_PATH = '/fsm/complaints-checklist';
const PAGE_SIZE = 10;
const CHECKLIST_TYPE_ALL = 'all';

const BADGE_STYLES = {
  'Internal Asset': 'bg-[#EDE9FE] text-[#5B4B8A]',
  'Field Service': 'bg-[#D5F5F6] text-[#0E7490]'
};

function templateKey(template) {
  return `${template.complaintTypeId}::${template.whoToCheck}`;
}

/** Group checklist items into one template row per whoToCheck. */
function buildTemplatesFromItems(
items,
{ productServiceId, productName, complaintTypeId, complaintTypeName })
{
  const byWho = new Map();
  for (const item of items || []) {
    const whoToCheck = String(item.whoToCheck || '').trim();
    if (!whoToCheck) continue;
    const whoKey = whoToCheck.toLowerCase();
    if (!byWho.has(whoKey)) {
      byWho.set(whoKey, { whoToCheck, checklistItems: [] });
    }
    byWho.get(whoKey).checklistItems.push(item);
  }

  return [...byWho.values()].map(({ whoToCheck, checklistItems }) => {
    const sorted = [...checklistItems].sort(
      (a, b) => (a.sequenceOrder || 0) - (b.sequenceOrder || 0)
    );
    return {
      id: `${complaintTypeId}::${whoToCheck}`,
      complaintTypeId,
      complaintTypeName,
      productServiceId,
      productName,
      whoToCheck,
      checklistItems: sorted,
      steps: sorted.length,
      stepCount: sorted.length
    };
  });
}

const pagerBtn =
'h-8 w-8 grid place-items-center rounded-lg border border-[var(--color-text-border)] text-[var(--color-text-secondary)] hover:bg-[var(--color-workspace)]/40 hover:text-[var(--color-primary)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed';

function TemplatesPagination({ page, totalPages, totalItems, pageSize, onPageChange }) {
  if (totalItems <= 0) return null;

  const start = (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, totalItems);

  return (
    <div className="flex items-center justify-between px-4 sm:px-6 py-3 border-t border-[var(--color-text-border)]/60 text-xs text-[var(--color-text-secondary)]">
      <span>
        <Trans i18nKey="pagination.showing" /> {start}-{end} <Trans i18nKey="common.of" /> {totalItems}
      </span>
      <div className="flex items-center gap-1">
        <button
          type="button"
          disabled={page <= 1}
          onClick={() => onPageChange(Math.max(1, page - 1))}
          aria-label={i18n.t("pagination.previousPage")}
          className={pagerBtn}>
          
          ‹
        </button>
        <span className="px-2 text-xs font-semibold text-foreground">
          {page} / {Math.max(1, totalPages)}
        </span>
        <button
          type="button"
          disabled={page >= totalPages}
          onClick={() => onPageChange(Math.min(totalPages, page + 1))}
          aria-label={i18n.t("pagination.nextPage")}
          className={pagerBtn}>
          
          ›
        </button>
      </div>
    </div>);

}

function ChecklistTypeBadge({ whoToCheck }) {
  const badgeCls = BADGE_STYLES[whoToCheck] || 'bg-sky-100 text-sky-800';
  return (
    <span
      className={cn(
        'inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold whitespace-nowrap',
        badgeCls
      )}>
      
      {whoToCheck || '—'}
    </span>);

}

export function FsmChecklistsListPage() {
  const navigate = useNavigate();

  const [systems, setSystems] = useState([]);
  const [systemsLoading, setSystemsLoading] = useState(true);
  const [systemsError, setSystemsError] = useState('');
  const [selectedSystemId, setSelectedSystemId] = useState('');

  const [complaints, setComplaints] = useState([]);
  const [complaintsLoading, setComplaintsLoading] = useState(false);
  const [complaintsError, setComplaintsError] = useState('');
  const [selectedComplaintId, setSelectedComplaintId] = useState('');

  const [checklistTypeFilter, setChecklistTypeFilter] = useState(CHECKLIST_TYPE_ALL);

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [deletingKey, setDeletingKey] = useState('');
  const [toast, setToast] = useState(null);
  const [page, setPage] = useState(1);

  const selectedSystem = useMemo(
    () => systems.find((s) => String(s.id) === String(selectedSystemId)) || null,
    [systems, selectedSystemId]
  );

  const selectedComplaint = useMemo(
    () =>
    complaints.find((c) => String(c.id) === String(selectedComplaintId)) || null,
    [complaints, selectedComplaintId]
  );

  // Load systems — default to the first
  useEffect(() => {
    let cancelled = false;
    setSystemsLoading(true);
    setSystemsError('');

    searchProductServices({
      page: 0,
      size: 100,
      sortBy: 'productServiceId',
      sortDir: 'ASC'
    })
    .then((list) => {
      if (cancelled) return;
      setSystems(list);
      if (list.length > 0) {
        setSelectedSystemId((prev) => prev || String(list[0].id));
      }
    })
    .catch((err) => {
      console.error('Failed to load systems:', err);
      if (!cancelled) {
        setSystems([]);
        setSystemsError(
          err.status === 401 ?
          'Session expired. Please refresh and try again.' :
          err.message || 'Failed to load systems.'
        );
      }
    })
    .finally(() => {
      if (!cancelled) setSystemsLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, []);

  // Load complaint types when system changes — default to the first
  useEffect(() => {
    if (!selectedSystemId) {
      setComplaints([]);
      setSelectedComplaintId('');
      return;
    }

    const system =
    systems.find((s) => String(s.id) === String(selectedSystemId)) || null;
    // Wait until systems list is available so we use the correct productServiceId
    if (systems.length === 0) return;

    let cancelled = false;
    setComplaintsLoading(true);
    setComplaintsError('');
    setSelectedComplaintId('');
    setComplaints([]);

    searchComplaintsByProduct({
      productServiceId: system?.productServiceId ?? selectedSystemId,
      page: 0,
      size: 10,
      sortBy: 'complaintTypeId',
      sortDir: 'ASC'
    })
    .then((list) => {
      if (cancelled) return;
      setComplaints(list);
      if (list.length > 0) {
        setSelectedComplaintId(String(list[0].id));
      }
    })
    .catch((err) => {
      console.error('Failed to load complaint types:', err);
      if (!cancelled) {
        setComplaints([]);
        setSelectedComplaintId('');
        setComplaintsError(
          err.status === 401 ?
          'Session expired. Please refresh and try again.' :
          err.message || 'Failed to load complaint types.'
        );
      }
    })
    .finally(() => {
      if (!cancelled) setComplaintsLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [selectedSystemId, systems]);

  const loadTemplates = useCallback(async () => {
    if (!selectedComplaintId) {
      setTemplates([]);
      setError('');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');
    try {
      const items = await fetchChecklistItemsByComplaintType(selectedComplaintId);
      const rows = buildTemplatesFromItems(items, {
        productServiceId: selectedSystem?.productServiceId ?? selectedSystemId,
        productName: selectedSystem?.name || '—',
        complaintTypeId: selectedComplaint?.complaintTypeId ?? selectedComplaintId,
        complaintTypeName: selectedComplaint?.name || '—'
      });
      setTemplates(rows);
      setPage(1);
    } catch (err) {
      console.error('Failed to load checklist templates:', err);
      setTemplates([]);
      setError(
        err.status === 401 ?
        'Session expired. Please refresh and try again.' :
        err.message || 'Failed to load checklist templates.'
      );
    } finally {
      setLoading(false);
    }
  }, [
  selectedComplaintId,
  selectedComplaint,
  selectedSystem,
  selectedSystemId]
  );

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const filteredTemplates = useMemo(() => {
    if (checklistTypeFilter === CHECKLIST_TYPE_ALL) return templates;
    return templates.filter(
      (t) =>
      String(t.whoToCheck || '').toLowerCase() ===
      String(checklistTypeFilter).toLowerCase()
    );
  }, [templates, checklistTypeFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredTemplates.length / PAGE_SIZE));

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  useEffect(() => {
    setPage(1);
  }, [checklistTypeFilter]);

  const pagedRows = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filteredTemplates.slice(start, start + PAGE_SIZE);
  }, [filteredTemplates, page]);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
  };

  const handleSystemChange = (value) => {
    setSelectedSystemId(value);
    setPage(1);
  };

  const handleComplaintChange = (value) => {
    setSelectedComplaintId(value);
    setPage(1);
  };

  const handleCreate = () => {
    navigate(`${LIST_PATH}/add`);
  };

  const handleEdit = (template) => {
    if (deletingKey) return;
    navigate(`${LIST_PATH}/add`, {
      state: {
        mode: 'edit',
        productServiceId: template.productServiceId,
        complaintTypeId: template.complaintTypeId,
        productName: template.productName,
        complaintTypeName: template.complaintTypeName,
        whoToCheck: template.whoToCheck,
        checklistItems: template.checklistItems
      }
    });
  };

  const handleDelete = async (template) => {
    const key = templateKey(template);
    if (deletingKey) return;
    if (!(template.stepCount || template.steps)) {
      showToast('No checklist items to delete.', 'error');
      return;
    }
    if (
    !window.confirm(
      `Delete the "${template.whoToCheck}" template for "${template.productName} • ${template.complaintTypeName}"? This cannot be undone.`
    ))
    {
      return;
    }

    setDeletingKey(key);
    try {
      await deleteChecklistTemplate({
        complaintTypeId: template.complaintTypeId,
        whoToCheck: template.whoToCheck,
        checklistItems: template.checklistItems
      });
      setTemplates((prev) => prev.filter((item) => templateKey(item) !== key));
      showToast('Checklist template deleted successfully.');
    } catch (err) {
      console.error('Failed to delete checklist template:', err);
      showToast(
        err.status === 401 ?
        'Session expired. Please refresh and try again.' :
        err.message || 'Failed to delete checklist template.',
        'error'
      );
    } finally {
      setDeletingKey('');
    }
  };

  const filtersLoading = systemsLoading || complaintsLoading;
  const filterError = systemsError || complaintsError;

  return (
    <AppLayout>
      {toast &&
      <div
        className={cn(
          'fixed bottom-6 right-6 z-50 p-4 rounded-xl shadow-lg flex items-center gap-3 animate-in fade-in slide-in-from-bottom-5 duration-300 text-white',
          toast.type === 'error' ? 'bg-rose-500' : 'bg-emerald-500'
        )}>
        
          <span className="text-sm font-semibold">{toast.message}</span>
          <button type="button" onClick={() => setToast(null)} className="hover:opacity-85 text-white/90">
            <X className="h-4 w-4" />
          </button>
        </div>
      }

      <PageBackLink to="/" />
      <PageHeader
        title={i18n.t("fsm.checklistTemplates")}
        action={
        <button type="button" onClick={handleCreate} className={ui.primaryBtn}>
            <Plus className="h-4 w-4" />
            <Trans i18nKey="formActions.createTemplate" />
          </button>
        } />
      

      <ContentCard className="mb-4" bodyClassName="p-4 sm:p-5">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="space-y-2">
            <label className="text-xs font-semibold text-foreground/80"><Trans i18nKey="fsm.system" /></label>
            <Select
              value={selectedSystemId || undefined}
              onValueChange={handleSystemChange}
              disabled={systemsLoading || systems.length === 0}>
              
              <SelectTrigger className="h-11 rounded-xl border-[var(--color-text-border)] bg-white text-sm">
                <SelectValue
                  placeholder={systemsLoading ? 'Loading systems...' : 'Select system'} />
                
              </SelectTrigger>
              <SelectContent className="max-h-60 overflow-y-auto">
                {systems.map((system) =>
                <SelectItem key={system.id} value={String(system.id)}>
                    {system.name}
                  </SelectItem>
                )}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <label className="text-xs font-semibold text-foreground/80"><Trans i18nKey="fsm.complaintType" /></label>
            <Select
              value={selectedComplaintId || undefined}
              onValueChange={handleComplaintChange}
              disabled={
              !selectedSystemId ||
              complaintsLoading ||
              complaints.length === 0
              }>
              
              <SelectTrigger className="h-11 rounded-xl border-[var(--color-text-border)] bg-white text-sm">
                <SelectValue
                  placeholder={
                  !selectedSystemId ?
                  'Select a system first' :
                  complaintsLoading ?
                  'Loading complaint types...' :
                  complaints.length === 0 ?
                  'No complaint types' :
                  'Select complaint type'
                  } />
                
              </SelectTrigger>
              <SelectContent className="max-h-60 overflow-y-auto">
                {complaints.map((complaint) =>
                <SelectItem key={complaint.id} value={String(complaint.id)}>
                    {complaint.name}
                  </SelectItem>
                )}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <label className="text-xs font-semibold text-foreground/80"><Trans i18nKey="fsm.checklistType" /></label>
            <Select
              value={checklistTypeFilter}
              onValueChange={setChecklistTypeFilter}>
              
              <SelectTrigger className="h-11 rounded-xl border-[var(--color-text-border)] bg-white text-sm">
                <SelectValue placeholder={i18n.t("fsm.allChecklistTypes")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={CHECKLIST_TYPE_ALL}><Trans i18nKey="common.all" /></SelectItem>
                {CHECKLIST_WHO_TO_CHECK.map((opt) =>
                <SelectItem key={opt} value={opt}>
                    {opt}
                  </SelectItem>
                )}
              </SelectContent>
            </Select>
          </div>
        </div>
        {filterError ?
        <p className="mt-3 text-xs text-[var(--color-error)] font-medium">{filterError}</p> :
        null}
      </ContentCard>

      <ContentCard>
        {filtersLoading || loading ?
        <LoadingState message="Loading checklist templates..." /> :
        filterError && systems.length === 0 ?
        <EmptyState
          icon={ClipboardList}
          title={i18n.t("validation.unableToLoadFilters")}
          message={filterError} /> :

        !selectedSystemId ?
        <EmptyState
          icon={ClipboardList}
          title={i18n.t("fsm.noSystems")}
          message="No systems are available to filter checklist templates." /> :

        !selectedComplaintId ?
        <EmptyState
          icon={ClipboardList}
          title={i18n.t("fsm.noComplaintTypes")}
          message="This system has no complaint types yet." /> :

        error ?
        <EmptyState
          icon={ClipboardList}
          title={i18n.t("validation.unableToLoadChecklistTemplates")}
          message={error} /> :

        filteredTemplates.length === 0 ?
        <EmptyState
          icon={ClipboardList}
          title={i18n.t("fsm.noChecklistTemplates")}
          message={
          checklistTypeFilter === CHECKLIST_TYPE_ALL ?
          'No checklist templates for this system and complaint type.' :
          `No "${checklistTypeFilter}" checklist for this complaint type.`
          } /> :


        <>
            {/* Mobile cards */}
            <div className="md:hidden divide-y divide-[var(--color-text-border)]/35">
              {pagedRows.map((template) => {
              const key = templateKey(template);
              const steps = template.steps ?? template.stepCount ?? 0;
              const busy = deletingKey === key;
              return (
                <div
                  key={key}
                  className={cn('px-4 py-4 flex flex-col gap-3', busy && 'opacity-60')}>
                  
                    <button
                    type="button"
                    onClick={() => handleEdit(template)}
                    disabled={busy}
                    className="text-left flex flex-col gap-2 cursor-pointer disabled:cursor-wait">
                    
                      <div className="flex items-center gap-2.5 flex-wrap">
                        <ChecklistTypeBadge whoToCheck={template.whoToCheck} />
                        <span className="text-[12px] font-medium text-[var(--color-text-secondary)]">
                          {steps} {steps === 1 ? 'item' : 'items'}
                        </span>
                      </div>
                      <p className="text-sm font-bold text-foreground">
                        {template.productName || '—'}
                        <span className="text-gray-400 font-semibold"> • </span>
                        {template.complaintTypeName || '—'}
                      </p>
                    </button>
                    <div className="flex items-center gap-2">
                      <button
                      type="button"
                      onClick={() => handleEdit(template)}
                      disabled={busy}
                      className="h-9 w-9 grid place-items-center rounded-xl border border-gray-200 bg-white text-gray-500 hover:text-gray-800 hover:bg-gray-50 transition cursor-pointer disabled:opacity-50"
                      title={i18n.t("common.edit")}>
                      
                        <Edit3 className="h-4 w-4" />
                      </button>
                      <button
                      type="button"
                      onClick={() => handleDelete(template)}
                      disabled={busy || steps === 0}
                      className="h-9 w-9 grid place-items-center rounded-xl border border-gray-200 bg-white text-rose-500 hover:bg-rose-50 transition cursor-pointer disabled:opacity-40"
                      title={i18n.t("formActions.deleteTemplate")}>
                      
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>);

            })}
            </div>

            {/* Desktop table */}
            <div className="hidden md:block w-full overflow-x-auto">
              <table className="w-full text-sm min-w-[720px]">
                <thead>
                  <tr className={ui.tableHead}>
                    <th className="px-4 py-3 font-semibold whitespace-nowrap"><Trans i18nKey="fsm.checklistType" /></th>
                    <th className="px-4 py-3 font-semibold whitespace-nowrap"><Trans i18nKey="fsm.system" /></th>
                    <th className="px-4 py-3 font-semibold whitespace-nowrap"><Trans i18nKey="fsm.complaintType" /></th>
                    <th className="px-4 py-3 font-semibold whitespace-nowrap"><Trans i18nKey="fsm.checklistItems" /></th>
                    <th className="px-4 py-3 font-semibold text-right whitespace-nowrap"><Trans i18nKey="common.action" /></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--color-text-border)]/35">
                  {pagedRows.map((template) => {
                  const key = templateKey(template);
                  const steps = template.steps ?? template.stepCount ?? 0;
                  const busy = deletingKey === key;
                  return (
                    <tr
                      key={key}
                      onClick={() => !busy && handleEdit(template)}
                      className={cn(
                        'hover:bg-[var(--color-workspace)]/20 transition-colors cursor-pointer',
                        busy && 'opacity-60'
                      )}>
                      
                        <td className="px-4 py-3.5 whitespace-nowrap">
                          <ChecklistTypeBadge whoToCheck={template.whoToCheck} />
                        </td>
                        <td className="px-4 py-3.5 font-semibold text-foreground whitespace-nowrap">
                          {template.productName || '—'}
                        </td>
                        <td className="px-4 py-3.5 text-[var(--color-text-secondary)] whitespace-nowrap">
                          {template.complaintTypeName || '—'}
                        </td>
                        <td className="px-4 py-3.5 whitespace-nowrap">
                          <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-[var(--color-workspace)]/60 text-[var(--color-primary)] text-xs font-semibold">
                            {steps} {steps === 1 ? 'Step' : 'Steps'}
                          </span>
                        </td>
                        <td className="px-4 py-3.5 text-right whitespace-nowrap">
                          <div className="inline-flex items-center gap-2">
                            <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEdit(template);
                            }}
                            disabled={busy}
                            className="h-8 w-8 grid place-items-center rounded-lg border border-[var(--color-text-border)] bg-white text-[var(--color-text-secondary)] hover:text-[var(--color-primary)] hover:bg-[var(--color-workspace)]/40 transition cursor-pointer disabled:opacity-50"
                            title={i18n.t("common.edit")}>
                            
                              <Edit3 className="h-3.5 w-3.5" />
                            </button>
                            <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(template);
                            }}
                            disabled={busy || steps === 0}
                            className="h-8 w-8 grid place-items-center rounded-lg border border-[var(--color-text-border)] bg-white text-rose-500 hover:bg-rose-50 transition cursor-pointer disabled:opacity-40"
                            title={i18n.t("formActions.deleteTemplate")}>
                            
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>);

                })}
                </tbody>
              </table>
            </div>

            <TemplatesPagination
            page={page}
            totalPages={totalPages}
            totalItems={filteredTemplates.length}
            pageSize={PAGE_SIZE}
            onPageChange={setPage} />
          
          </>
        }
      </ContentCard>
    </AppLayout>);

}
